import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ObjectiveService from '@apis/ObjectiveService';
import { objectiveFeedQueryKeys } from '@apis/queries/useObjectiveFeed';
import { monitorQueryKeys } from '@apis/queries/useMonitors';
import type { Objective, ObjectiveProgressDetail } from '@customTypes/objectiveFeed';

export const objectiveQueryKeys = {
  all: ['objectives'] as const,
  list: () => ['objectives', 'list'] as const,
};

export function useObjectives() {
  const {
    data: objectives = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Objective[]>({
    queryKey: objectiveQueryKeys.list(),
    queryFn: () => ObjectiveService.getObjectives(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return { objectives, isLoading, error, refetch };
}

export function useCreateObjective() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { title: string; description?: string; progress_detail?: ObjectiveProgressDetail; skip_kickoff?: boolean; discovery_questions?: string[]; kickoff_body?: string; skill_insights?: { skill_name: string; strategy_summary: string }[] }) =>
      ObjectiveService.createObjective(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectiveFeedQueryKeys.all });
    },
  });
}

export function useUpdateObjective() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; title?: string; description?: string }) =>
      ObjectiveService.updateObjective(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveQueryKeys.all });
    },
  });
}

export function useDeleteObjective() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ObjectiveService.deleteObjective(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveQueryKeys.all });
    },
  });
}

export function useRefreshObjectiveProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => ObjectiveService.refreshProgress(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveFeedQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectiveQueryKeys.all });
    },
  });
}

export function useTriggerHeartbeat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => ObjectiveService.triggerHeartbeat(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveFeedQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectiveQueryKeys.all });
    },
  });
}

export function useTriggerMonitorSweep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => ObjectiveService.triggerMonitorSweep(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: monitorQueryKeys.all });
    },
  });
}

export function useTriggerWeeklyLearnings() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerWeeklyLearnings(),
  });
}

export function useTriggerDailyPlan() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerDailyPlan(),
  });
}

export function useTriggerStrategicReview() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerStrategicReview(),
  });
}

export function useTriggerAuditSweep() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerAuditSweep(),
  });
}

export function useTriggerSynthesisAudit() {
  return useMutation({
    mutationFn: (audit?: string) => ObjectiveService.triggerSynthesisAudit(audit),
  });
}

export function useTriggerFridayWrap() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerFridayWrap(),
  });
}

export function useTriggerCreativeRun() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerCreativeRun(),
  });
}

export function useTriggerCreativeRefresh() {
  return useMutation({
    mutationFn: () => ObjectiveService.triggerCreativeRefresh(),
  });
}

export function useResetCreativeRun() {
  return useMutation({
    mutationFn: () => ObjectiveService.resetCreativeRun(),
  });
}

